import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Heart, Loader2, AlertCircle } from "lucide-react";
import { IMAGES, WAITLIST, scrollToId } from "../../lib/content";
import { joinWaitlist, formatApiError } from "../../lib/api";
import { Reveal } from "./Reveal";
import { CountLine } from "./CountLine";
import { InviteBanner } from "./InviteBanner";
import { PartnerInvite } from "./PartnerInvite";
import { ShareCard } from "./ShareCard";
import { StoryBox } from "./StoryBox";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const Status = ({ state }) => {
  if (state.type === "created")
    return (
      <p data-testid="waitlist-success-message" className="status-pill bg-[#EAF0EC] text-[#2F4537]">
        <CheckCircle2 className="h-5 w-5 shrink-0" /> You're on the list! Keep an eye on your inbox for a little hello from us.
      </p>
    );
  if (state.type === "exists")
    return (
      <p data-testid="waitlist-duplicate-message" className="status-pill bg-[#FAF3E8] text-[#6B4A1F]">
        <Heart className="h-5 w-5 shrink-0" /> You're already on the list — we'll be in touch soon.
      </p>
    );
  return null;
};

export const Waitlist = ({ count, onJoined, invite, referral }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [state, setState] = useState({ type: "idle" });
  const [loading, setLoading] = useState(false);
  const [joinedEmail, setJoinedEmail] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!invite && !referral) return;
    scrollToId("waitlist");
    const t = setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 900);
    return () => clearTimeout(t);
  }, [invite, referral]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ type: "idle" });
    setJoinedEmail("");
    if (!EMAIL_RE.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const normalized = email.trim().toLowerCase();
      const { data } = await joinWaitlist(normalized, invite?.token, referral?.token);
      setState({ type: data.status });
      setJoinedEmail(normalized);
      if (data.status === "created") {
        setEmail("");
        onJoined?.();
      }
    } catch (err) {
      setError(err.response?.status === 422 ? "Please enter a valid email address." : formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="waitlist" data-testid="waitlist-section" className="section bg-[#FDFBF7] pt-0">
      <div className="mx-auto max-w-7xl px-4 sm:px-10 lg:px-16">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] sm:rounded-[2.5rem] bg-[#2C221E]">
            <img src={IMAGES.meadow.src} alt={IMAGES.meadow.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#2C221E]/90 via-[#3A2A20]/75 to-[#2C221E]/30" />
            <div className="relative px-6 py-16 sm:px-14 sm:py-24 lg:px-20 lg:py-28 max-w-2xl">
              {invite && <InviteBanner name={invite.name} />}
              {referral && (
                <InviteBanner testId="referral-banner" name={referral.name || "A friend"}
                  suffix={`${referral.name ? "are" : "is"} joining Woven — join them`} />
              )}
              <h2 data-testid="waitlist-heading" className="font-serif text-4xl sm:text-5xl tracking-tight text-[#FDFBF7] leading-[1.1]">
                {WAITLIST.heading}
              </h2>
              <p data-testid="waitlist-subheading" className="mt-6 text-base sm:text-lg leading-relaxed text-[#F4EBDD]/90">{WAITLIST.sub}</p>
              <CountLine count={count} testId="waitlist-counter" className="mt-6 text-[#F2C98F]" />

              <form onSubmit={onSubmit} noValidate data-testid="waitlist-form" className="mt-10">
                <div className={`flex flex-col sm:flex-row gap-3 rounded-[1.75rem] sm:rounded-full bg-[#FDFBF7] p-2 shadow-[0_20px_50px_rgba(0,0,0,0.25)] transition-shadow duration-500 ${invite || referral ? "ring-4 ring-[#F2C98F]/80" : ""}`}>
                  <label htmlFor="waitlist-email" className="sr-only">Email address</label>
                  <input
                    ref={inputRef}
                    id="waitlist-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@family.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
                    aria-invalid={!!error}
                    aria-describedby="waitlist-error"
                    data-testid="waitlist-email-input"
                    className="min-w-0 flex-1 rounded-full bg-transparent px-5 py-3.5 text-base text-[#2C221E] placeholder:text-[#8A7A72] outline-none"
                  />
                  <button type="submit" disabled={loading} data-testid="waitlist-submit-button" className="btn-primary justify-center">
                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {WAITLIST.cta}
                  </button>
                </div>
                {error && (
                  <p id="waitlist-error" role="alert" data-testid="waitlist-error-message" className="mt-4 flex items-center gap-2 text-sm font-medium text-[#FFB59A]">
                    <AlertCircle className="h-4 w-4 shrink-0" /> {error}
                  </p>
                )}
              </form>

              <div className="mt-5" aria-live="polite"><Status state={state} /></div>
              {joinedEmail && (
                <>
                  <PartnerInvite inviterEmail={joinedEmail} />
                  <ShareCard email={joinedEmail} />
                  <StoryBox email={joinedEmail} />
                </>
              )}
              <p data-testid="waitlist-no-spam" className="mt-6 text-sm tracking-wide text-[#F4EBDD]/75">{WAITLIST.note}</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
